import { BatchNeighbors, PoState, ProductionBatch, Scan, ScanStatus } from "../../model";
import { NgxsDataEntityCollectionsRepository } from "@angular-ru/ngxs/repositories";
import { Computed, DataAction, Payload, StateRepository } from "@angular-ru/ngxs/decorators";
import { NgxsAfterBootstrap, State } from "@ngxs/store";
import { createEntityCollections, EntityCollections } from "@angular-ru/cdk/entity";
import { Injectable } from "@angular/core";
import { defaultEntityCollation, EntityCollation, singleRequest } from "../index";
import { EntityIdType } from "@angular-ru/cdk/entity";
import { BackendService } from "../../services/backend";
import { distinct, mergeMap, Observable, of, tap } from "rxjs";
import { map } from "rxjs/operators";
import { AuthenticationState } from "../auth/auth.state";

@StateRepository()
@State<EntityCollections<ProductionBatch, EntityIdType, EntityCollation<ProductionBatch>>>({
  name: 'batches',
  defaults: {
    ...createEntityCollections(),
    ...defaultEntityCollation()
  }
})
@Injectable()
export class ProductionBatchState extends NgxsDataEntityCollectionsRepository<ProductionBatch, EntityIdType, EntityCollation<ProductionBatch>> implements NgxsAfterBootstrap {

  constructor(private backend: BackendService,
    private authState: AuthenticationState) {
    super()
    this.setComparator({
      sortBy: "inspectionSequence",
      sortByOrder: "desc"
    })
  }

  override ngxsAfterBootstrap() {
    super.ngxsAfterBootstrap();
    //if (!this.authState.hasRole(UserRole.Admin))
    this.backend.getBatchesUpdated()
      .subscribe(d => {
        this.upsertOne(d);
      }
      )
  }

  public lastPending(): Observable<ProductionBatch | undefined> {
    return this.entitiesArray$
      .pipe(
        map(batches => batches.find(b => b.state == PoState.Pending)),
        mergeMap(b => b ? of(b).pipe(distinct(b => b.id)) : of(b))
      )
  }

  @Computed()
  public get allLoaded(): ProductionBatch[] {
    return this.entitiesArray;
  }

  @Computed()
  public get focused(): ProductionBatch | null {
    const result = this.snapshot.focusOnId ? this.selectOne(this.snapshot.focusOnId) : null
    console.info("New focused", result)
    return result;
  }

  @DataAction()
  public focusOn(@Payload('id') id: string): Observable<ProductionBatch> {
    this.ctx.patchState(
      {
        focusOnId: id
      }
    )
    return this.backend.getBatch(id).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public split(@Payload('id') id: string, @Payload('quantity') quantity: number): Observable<ProductionBatch> {
    return this.backend.splitBatch(id, quantity).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public suspend(@Payload('id') id: string): Observable<ProductionBatch> {
    return this.backend.suspendBatch(id).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public delete(@Payload('id') id: string): Observable<void> {
    return this.backend.deleteBatch(id).pipe(
      tap(() => {
        this.removeOne(id);
      }),
      singleRequest()
    )
  }

  @DataAction()
  public suspendAll(): Observable<ProductionBatch[]> {
    return this.backend.suspendAllBatches().pipe(
      tap(b => {
        this.upsertMany(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public truncate(@Payload('id') id: string, @Payload('quantity') quantity: number): Observable<ProductionBatch> {
    return this.backend.truncateBatch(id, quantity).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public createBypassBatch(@Payload('quantity') quantity: number): Observable<ProductionBatch> {
    return this.backend.createBypassBatch(quantity).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public attach(@Payload('id') id: string, @Payload('to') to: string): Observable<ProductionBatch> {
    return this.backend.attachBatch(id, to).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public bypass(@Payload('id') id: string): Observable<ProductionBatch> {
    return this.backend.bypassBatch(id).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public inspect(@Payload('id') id: string): Observable<ProductionBatch> {
    return this.backend.inspectBatch(id).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public abort(@Payload('id') id: string): Observable<ProductionBatch> {
    return this.backend.abortBatch(id).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }
  @DataAction()
  ignore(@Payload('id') id: string): Observable<ProductionBatch> {
    return this.backend.ignoreBatch(id).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public updateStatus(@Payload('scan') scan: Scan, @Payload('status') status: ScanStatus, @Payload('reason') reason?: string | null): Observable<ProductionBatch> {
    return this.backend.updateScanStatus(scan.productionBatchId, scan.id, status, reason).pipe(
      tap(b => {
        this.upsertOne(b);
      }
      ),
      singleRequest())
  }

  @DataAction()
  public loadPage(@Payload('pageIndex') pageIndex = 1, @Payload('pageSize') pageSize = 50, @Payload('poNamePrefix') poNamePrefix?: string, @Payload('currentOnly') currentOnly?: boolean) {
    this.ctx.patchState(
      {
        pageIndex,
        pageSize,
        isLoading: true
      }
    )
    return this.backend.getBatchesPage(pageIndex, pageSize, currentOnly, poNamePrefix).pipe(
      tap(batches => {
        this.upsertMany(batches.items);
        this.ctx.patchState(
          {
            isLoading: false,
            totalCount: batches.totalCount
          }
        )
      }),
      singleRequest()
    )
  }


  @DataAction()
  public getPage(@Payload('pageIndex') pageIndex = 1, @Payload('pageSize') pageSize = 50, @Payload('poNamePrefix') poNamePrefix?: string, @Payload('currentOnly') currentOnly?: boolean) {
    this.ctx.patchState(
      {
        pageIndex,
        pageSize,
        isLoading: true
      }
    )
    return this.backend.getBatchesPage(pageIndex, pageSize, currentOnly, poNamePrefix).pipe(
      tap(batches => {
        this.setAll(batches.items);
        this.ctx.patchState(
          {
            isLoading: false,
            totalCount: batches.totalCount
          }
        )
      }),
      singleRequest()
    )
  }

  @DataAction()
  public queryPage(@Payload('payload') payload: any) {
    this.ctx.patchState(
      {
        pageIndex: payload.pageIndex || 1,
        pageSize: payload.pageSize || 50,
        isLoading: true
      }
    )
    return this.backend.queryBatchesPage(payload).pipe(
      tap(batches => {
        this.setAll(batches.items);
        this.ctx.patchState(
          {
            isLoading: false,
            totalCount: batches.totalCount
          }
        )
      }),
      singleRequest()
    )
  }

  getAttachTargets(id: string): Observable<ProductionBatch[]> {
    return this.backend.getAttachTargets(id)
  }

  getNeighbors(id: string): Observable<BatchNeighbors> {
    return this.backend.getBatchNeighbors(id)
  }

  public refreshPage() {
    this.getPage(this.snapshot.pageIndex, this.snapshot.pageSize);
  }

  public updateScanDetails(updatedScan: Scan): void {
    const batch = this.selectOne(updatedScan.productionBatchId);
    if (!batch) return;
    const updatedBatch = {
      ...batch,
      scans: batch.scans.map(s => s.id === updatedScan.id ? { ...updatedScan, productionBatch: batch } : s)
    };
    this.upsertOne(updatedBatch);
  }

  public removeScanFromBatch(deletedScan: Scan): void {
    const batch = this.selectOne(deletedScan.productionBatchId);
    if (!batch) return;
    const updatedBatch = {
      ...batch,
      scans: batch.scans.filter(s => s.id !== deletedScan.id)
    };
    this.upsertOne(updatedBatch);
  }

}
